import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface DuplicatePageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourcePageId: string;
  onSuccess: (newPageId: string) => void;
}

export function DuplicatePageDialog({
  open,
  onOpenChange,
  sourcePageId,
  onSuccess,
}: DuplicatePageDialogProps) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const slug = slugify(name);

  const handleDuplicate = async () => {
    if (!name.trim() || !slug) return;
    setLoading(true);

    try {
      // 1. Create new page
      const { data: newPage, error: pageError } = await supabase
        .from("partner_landing_pages")
        .insert({
          slug,
          page_title: name.trim(),
          is_active: false,
        })
        .select("id")
        .single();

      if (pageError) throw pageError;

      // 2. Fetch source sections
      const { data: sections, error: secError } = await supabase
        .from("partner_landing_sections")
        .select("*")
        .eq("landing_page_id", sourcePageId)
        .order("order_index");

      if (secError) throw secError;

      // 3. Copy sections to new page
      if (sections && sections.length > 0) {
        const newSections = sections.map(({ id, created_at, updated_at, landing_page_id, ...rest }) => ({
          ...rest,
          landing_page_id: newPage.id,
        }));

        const { error: insertError } = await supabase
          .from("partner_landing_sections")
          .insert(newSections);

        if (insertError) throw insertError;
      }

      toast.success("LP duplicada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["admin-partner-pages"] });
      setName("");
      onOpenChange(false);
      onSuccess(newPage.id);
    } catch (err: any) {
      toast.error(err.message || "Erro ao duplicar LP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicar Landing Page</DialogTitle>
          <DialogDescription>
            Informe o nome do novo parceiro. A LP será criada como inativa.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="partner-name">Nome do parceiro</Label>
            <Input
              id="partner-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Data Academy Brasil"
            />
          </div>
          {slug && (
            <p className="text-sm text-muted-foreground">
              Slug: <span className="font-mono text-foreground">{slug}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleDuplicate} disabled={!name.trim() || loading}>
            {loading ? "Duplicando..." : "Duplicar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
